import React from 'react';
import { useSelector } from 'react-redux';
import { arrayBuilderPages } from 'platform/forms-system/src/js/patterns/array-builder';
import { titleUI } from 'platform/forms-system/src/js/web-component-patterns';
import { createArrayBuilderItemAddPath } from 'platform/forms-system/src/js/patterns/array-builder/helpers';

import { ConditionsIntroDescription } from '../../content/conditions';
import ConditionsSideNavResetAlert from '../../components/ConditionsSideNavResetAlert';
import {
  arrayOptions,
  hasRatedDisabilities,
  isEditFromContext,
  generateUniqueId as generateConditionId,
} from './shared/utils';
import summaryPage from './shared/summary';
import { remainingSharedPages } from './shared';
import conditionPage from './condition';
import { sweepOrphanedTEConditions } from '../../utils/reconcile-toxic-exposure-conditions';

/**
 * Hook that determines whether to display a warning about rated disabilities unavailability
 * @param {Object} formData - The form data object
 * @returns {boolean} True if a warning message should be shown, false otherwise
 *
 * `formData.ratedDisabilitiesFetchFailed` is set to `true` during the prefill if the lookup failed
 */
export const useShouldShowRatedDisabilitiesWarning = formData => {
  return Boolean(formData?.ratedDisabilitiesFetchFailed);
};

/**
 * Renders intro content with conditional warning for rated disabilities.
 * Reads formData directly from the Redux store via useSelector; takes no props.
 * @returns {JSX.Element}
 */
export const ConditionsIntroMarkup = () => {
  const formData = useSelector(state => state.form?.data);
  const showWarning = useShouldShowRatedDisabilitiesWarning(formData);

  return (
    <>
      <ConditionsIntroDescription />
      {showWarning && (
        <va-alert status="warning" class="vads-u-margin-top--0">
          <h3 slot="headline">
            Some tools or services aren’t working right now
          </h3>
          <p>Here’s what you can do while we fix the issue:</p>
          <ul className="vads-u-margin-top--0">
            <li>
              If you have rated disabilities and want to request an increase,
              check back in a few hours.
            </li>
            <li>
              If you’re adding a new disability to your claim, you can continue
              now.
            </li>
          </ul>
        </va-alert>
      )}
    </>
  );
};

ConditionsIntroMarkup.propTypes = {};

const ratedIntroPage = {
  uiSchema: {
    'ui:description': ({ formData }) => (
      <ConditionsSideNavResetAlert formData={formData} />
    ),
    // wrapping titleUI in the view field so alert will render above it
    'view:conditionsIntro': {
      ...titleUI('Add your disabilities and conditions'),
      'ui:description': ConditionsIntroMarkup,
    },
  },
  schema: {
    type: 'object',
    properties: {
      'view:conditionsIntro': {
        type: 'object',
        properties: {},
      },
    },
  },
};

/**
 * Builds the disability conditions array-builder workflow.
 *
 * @param {Object} [workflowOptions]
 * @param {boolean} [workflowOptions.monthYearOnlyConditionDate=false] - When
 *   true, collect only the month and year; otherwise, also collect the day.
 */
export const buildDisabilityConditionsWorkflow = (workflowOptions = {}) =>
  arrayBuilderPages(arrayOptions, (pageBuilder, helpers) => ({
    Intro: pageBuilder.introPage({
      title: 'Add your disabilities and conditions',
      path: 'conditions/orientation',
      uiSchema: ratedIntroPage.uiSchema,
      schema: ratedIntroPage.schema,
      onNavForward: props => {
        const { formData, setFormData, goPath, urlParams } = props;
        const { arrayPath } = arrayOptions;

        const items = formData?.[arrayPath] || [];

        // Backfill UUIDs for in-progress forms that don't have them
        // This ensures consistent behavior for both new and existing forms
        // Rated disabilities will use their ratedDisabilityId, new conditions get UUIDs
        const itemsWithIds = items.map(item => {
          // Skip if already has view:id or has ratedDisabilityId (which serves as stable ID)
          if (item['view:id'] || item.ratedDisabilityId) {
            return item;
          }
          // Backfill UUID for conditions that don't have any stable ID
          return {
            ...item,
            'view:id': generateConditionId(),
          };
        });

        // Update formData if any UUIDs were added
        const needsUpdate = itemsWithIds.some(
          (item, idx) => item !== items[idx],
        );

        if (needsUpdate) {
          setFormData({
            ...formData,
            [arrayPath]: itemsWithIds,
          });
        }

        const nextIndex = itemsWithIds.length;
        const hasUnaddedRated = hasRatedDisabilities(formData, undefined);

        // SPECIAL CASE: no rated disabilities AND no existing newDisabilities.
        // This is the "brand new claim, nothing rated" scenario.
        if (!hasUnaddedRated && nextIndex === 0) {
          const newItem = {
            condition: '', // will be filled on the NewCondition page
            'view:id': generateConditionId(),
          };

          const updatedItems = [...itemsWithIds, newItem];

          setFormData({
            ...formData,
            [arrayPath]: updatedItems,
          });

          const path = createArrayBuilderItemAddPath({
            path: 'conditions/:index/new-condition',
            index: 0,
            isReview: !!urlParams?.review,
            removedAllWarn: false,
          });

          return goPath(path);
        }

        // In all other cases (there ARE rated disabilities, or items already exist),
        // just use the normal intro navigation behavior.
        return helpers.navForwardIntro
          ? helpers.navForwardIntro(props)
          : helpers.navForwardSummary(props);
      },
    }),

    Summary: pageBuilder.summaryPage({
      title: 'Review your conditions',
      path: 'conditions/summary',
      uiSchema: summaryPage.uiSchema,
      schema: summaryPage.schema,
      onNavForward: props => {
        const { formData, setFormData } = props;
        const { arrayPath } = arrayOptions;

        // Defensive sweep before leaving the array-builder summary:
        // any toxicExposure.conditions key whose condition was edited or
        // deleted on an item page will not have been reconciled (V2 edit
        // mode bypasses pageConfig.updateFormData), and would otherwise
        // trip the "none + condition" validator on the TE conditions page.
        const reconciled = sweepOrphanedTEConditions(formData);
        if (reconciled !== formData) {
          setFormData(reconciled);
        }

        const items = formData?.[arrayPath] || [];
        const hasAnyItems = items.length > 0;

        const hasConditions = formData?.['view:hasConditions'];

        // GUARD: You can't leave this page with zero conditions.
        // If the user got here with an empty array (e.g., via "Finish this application later"),
        // force them into the add flow regardless of answering "No".
        if (!hasAnyItems) {
          const nextIndex = 0;

          // Determine whether we should start with rated pick page or new-condition page
          const hasUnaddedRated = hasRatedDisabilities(formData, nextIndex);

          // If no rated disabilities left to pick from, pre-seed so NewCondition can render safely
          if (!hasUnaddedRated) {
            const newItem = { condition: '', 'view:id': generateConditionId() };
            setFormData({
              ...formData,
              [arrayPath]: [newItem],
            });
          }

          const basePath = hasUnaddedRated
            ? 'conditions/:index/condition'
            : 'conditions/:index/new-condition';

          const path = createArrayBuilderItemAddPath({
            path: basePath,
            index: nextIndex,
            isReview: !!props.urlParams?.review,
            removedAllWarn: false,
          });

          return props.goPath(path);
        }

        // If they answered "No" AND we already have at least one condition,
        // allow them to move on.
        if (!hasConditions) {
          return helpers.navForwardSummary(props);
        }

        // If they answered "Yes", then go add another condition
        const nextIndex = items.length;
        const hasUnaddedRated = hasRatedDisabilities(formData, nextIndex);

        if (!hasUnaddedRated) {
          const newItem = { condition: '', 'view:id': generateConditionId() };
          setFormData({
            ...formData,
            [arrayPath]: [...items, newItem],
          });
        }

        const basePath = hasUnaddedRated
          ? 'conditions/:index/condition'
          : 'conditions/:index/new-condition';

        const path = createArrayBuilderItemAddPath({
          path: basePath,
          index: nextIndex,
          isReview: !!props.urlParams?.review,
          removedAllWarn: false,
        });

        return props.goPath(path);
      },
      updateFormData: summaryPage.updateFormData,
    }),

    Condition: pageBuilder.itemPage({
      title: 'Type of condition',
      path: `conditions/:index/condition`,
      depends: (formData, index, ctx) => {
        const noEdit = !isEditFromContext(ctx);
        const picked =
          formData?.newDisabilities?.[index]?.ratedDisability != null;

        const canAddMore = hasRatedDisabilities(formData, undefined);

        return noEdit && (picked || canAddMore);
      },
      itemFilter: item => item?.ratedDisability != null,
      uiSchema: conditionPage.uiSchema,
      schema: conditionPage.schema,
      updateFormData: conditionPage.updateFormData,
    }),

    ...remainingSharedPages(pageBuilder, helpers, workflowOptions),
  }));

// original all-claims (Form526v1) workflow: full month/day/year inputs
// (whereas v2 collects only month + year)
export const disabilityConditionsWorkflow = buildDisabilityConditionsWorkflow();
